import { useEffect, useRef, useCallback } from 'react'
import { getWidgetCable, releaseWidgetCable } from '@/lib/widgetCable'
import type { Consumer, Subscription } from '@rails/actioncable'

interface Agent {
  id: number
  name: string
  avatar_url: string | null
  online: boolean
}

interface EndUser {
  id: number
  name: string
}

interface Attachment {
  id: number
  signed_id: string
  filename: string
  content_type: string
  byte_size: number
  url: string
}

interface RepliedTo {
  id: number
  body: string | null
  author_type: 'Agent' | 'EndUser' | null
  author_name: string
  attachments_count: number
  deleted: boolean
  image_url?: string | null
}

interface Message {
  id: number
  conversation_id?: number
  body: string
  author_type: 'Agent' | 'EndUser' | null
  author: Agent | EndUser
  created_at: string
  deleted_at: string | null
  attachments: Attachment[]
  system_message_type?: 'away_notice' | 'email_capture' | null
  metadata?: Record<string, unknown> | null
  edited_at?: string | null
  replied_to?: RepliedTo | null
}

export interface TypingData {
  user_id: number
  user_name: string
  user_type: string
  avatar_url: string | null
  is_typing: boolean
}

interface AssignmentData {
  assigned_agent: Agent | null
}

// Discriminated union so TypeScript narrows `message` based on `type`
type BroadcastData =
  | { type: 'message'; message: Message }
  | { type: 'message_updated'; message: MessageUpdatedData }
  | { type: 'typing'; typing: TypingData }
  | { type: 'assignment_changed'; assigned_agent: Agent | null }

export interface MessageUpdatedData {
  id: number
  body: string
  system_message_type?: 'away_notice' | 'email_capture' | null
  metadata?: Record<string, unknown> | null
  edited_at?: string | null
}

interface UseWidgetChannelProps {
  conversationId: number | null
  apiUrl: string
  sessionToken: string
  onMessageReceived: (message: Message) => void
  onMessageUpdated?: (data: MessageUpdatedData) => void
  onTypingReceived?: (typingData: TypingData) => void
  onAssignmentChanged?: (data: AssignmentData) => void
}

export function useWidgetChannel({
  conversationId,
  apiUrl,
  sessionToken,
  onMessageReceived,
  onMessageUpdated,
  onTypingReceived,
  onAssignmentChanged,
}: UseWidgetChannelProps) {
  const cableRef = useRef<Consumer | null>(null)
  const subscriptionRef = useRef<Subscription | null>(null)

  // Use refs for callbacks to avoid recreating subscription
  const onMessageReceivedRef = useRef(onMessageReceived)
  const onMessageUpdatedRef = useRef(onMessageUpdated)
  const onTypingReceivedRef = useRef(onTypingReceived)
  const onAssignmentChangedRef = useRef(onAssignmentChanged)

  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived
    onMessageUpdatedRef.current = onMessageUpdated
    onTypingReceivedRef.current = onTypingReceived
    onAssignmentChangedRef.current = onAssignmentChanged
  }, [onMessageReceived, onMessageUpdated, onTypingReceived, onAssignmentChanged])

  useEffect(() => {
    // Don't subscribe if no conversation selected
    if (!conversationId || !sessionToken || !apiUrl) return

    // Get shared cable consumer (session token passed via subscription params, not URL)
    cableRef.current = getWidgetCable(apiUrl)

    // Subscribe to the conversation channel with session token for authentication
    const subscription = cableRef.current.subscriptions.create(
      {
        channel: 'ConversationChannel',
        conversation_id: conversationId,
        session_token: sessionToken,
      },
      {
        connected() {},
        disconnected() {},
        received(data: BroadcastData) {
          switch (data.type) {
            case 'message':
              onMessageReceivedRef.current(data.message)
              break
            case 'message_updated':
              onMessageUpdatedRef.current?.(data.message)
              break
            case 'typing':
              onTypingReceivedRef.current?.(data.typing)
              break
            case 'assignment_changed':
              onAssignmentChangedRef.current?.({ assigned_agent: data.assigned_agent })
              break
          }
        },
      }
    )

    subscriptionRef.current = subscription

    // Cleanup: unsubscribe when component unmounts or conversationId changes
    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
      releaseWidgetCable()
      cableRef.current = null
    }
  }, [conversationId, apiUrl, sessionToken])

  const notifyTyping = useCallback((isTyping: boolean) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.perform('typing', { is_typing: isTyping })
    }
  }, [])

  return { notifyTyping }
}
